import { flatten, forEachObjIndexed } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { setValue } from '@gef-ui/form-core/stream/transfer/control';

import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';

import { FIELDS, MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME } from './form';
import { getAgencyManagersAndSubordinates } from './selectors';
import {
	resetAgencyManagersAndSubordinates,
	saveAgencies,
	saveNotAssignedManagers,
	saveOriginalValues,
	savePristineStates,
	saveRegions,
	saveSegments,
} from './slice';

export const fetchRegionsAndSegments =
	({ setRegion, setSegment }) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(
			fetch.actions.get(fetch.constants.urls.settingsAllowedRegionsAndSegments, {
				onSuccess: [
					(response) => () => {
						if (!response.body) return;

						const { regions = [], segments = [] } = response.body;
						dispatch(saveRegions(regions));
						dispatch(saveSegments(segments));

						//pre-setting the initial values to form
						sendControlNext(
							MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME,
							setValue({ fieldName: FIELDS.region, value: regions[0]?.id }),
						);

						setRegion(regions[0]);
						sendControlNext(
							MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME,
							setValue({ fieldName: FIELDS.segment, value: segments[0]?.id }),
						);
						setSegment(segments[0]);
					},
				],
			}),
		);
	};

export const startupMarketPlaceAgenciesTab =
	({ setRegion, setSegment }) =>
	(dispatch) => {
		dispatch(fetchRegionsAndSegments({ setRegion, setSegment }));
	};

export const fetchMarketPlaceAgencies =
	({ region, segment }) =>
	(dispatch) => {
		dispatch(
			fetch.actions.get(fetch.constants.urls.settingsMarketPlaceAgencies, {
				replaceParams: { regionId: region, segmentId: segment },
				onSuccess: [
					(response) => () => {
						if (!response.body) return;
						const { areas = [], notAssignedManagers = [] } = response.body;

						dispatch(
							saveOriginalValues({
								areas,
								notAssignedManagers,
								agencyManagersAndSubordinates: EMPTY_VALUES.OBJECT,
							}),
						);
						dispatch(saveAgencies(areas));
						dispatch(saveNotAssignedManagers(notAssignedManagers));

						const initialPristineStates = {};
						areas.forEach((area) => (initialPristineStates[area?.areaId] = true));
						dispatch(savePristineStates(initialPristineStates));
						dispatch(resetAgencyManagersAndSubordinates());
					},
				],
			}),
		);
	};

export const refetchOriginalValues =
	() =>
	async (dispatch, getState, { getFormData }) => {
		// refetching the real original values and setting them as new "working" data
		const formData = await getFormData(MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME);
		dispatch(fetchMarketPlaceAgencies({ region: formData?.region, segment: formData?.segment }));
	};

export const sendAgenciesChangesToDb =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(MARKET_PLACE_AGENCIES_SETTINGS_FORM_NAME);
		const editedAgencies = getAgencyManagersAndSubordinates(getState());

		if (!isNilOrEmpty(editedAgencies)) {
			const editedAgenciesArr = [];
			forEachObjIndexed((managers, key) => {
				const agencyAllSubordinates = flatten(managers.map((manager) => manager?.subordinates));
				const onlyAssignedSubordinates = agencyAllSubordinates.filter((subordinate) => subordinate?.assigned);
				const onlyAssignedSubordinatesLogins = onlyAssignedSubordinates.map(
					(onlyAssigned) => onlyAssigned?.userLogin,
				);

				const area = {
					areaId: key,
					logins: onlyAssignedSubordinatesLogins,
				};

				editedAgenciesArr.push(area);
			}, editedAgencies);

			const body = {
				regionId: formData?.region,
				segment: formData?.segment,
				areas: editedAgenciesArr,
			};

			dispatch(
				fetch.actions.put(fetch.constants.urls.saveMarketPlaceAgenciesChanges, {
					body,
					onSuccess: [
						() => () => {
							dispatch(refetchOriginalValues());
							dispatch(resetAgencyManagersAndSubordinates());
						},
					],
				}),
			);
		}
	};
